/** Image encoders used for data export: TIFF (uint8/uint16/float32) and PFM. */

export interface TiffImage {
  width: number;
  height: number;
  channels: 1 | 3;
  data: Uint8Array | Uint16Array | Float32Array;
  description?: string;
  software?: string;
  /** Pixels per centimetre, written as XResolution/YResolution. */
  pixelsPerCm?: number;
  /** Embedded ICC profile (tag 34675). */
  icc?: Uint8Array;
  /** TIFF Orientation (tag 274), 1..8. */
  orientation?: number;
}

/** Baseline little-endian, uncompressed, single-strip TIFF. */
export function encodeTiff(img: TiffImage): Uint8Array {
  const { width, height, channels, data } = img;
  const bits = data instanceof Uint8Array ? 8 : data instanceof Uint16Array ? 16 : 32;
  const sampleFormat = data instanceof Float32Array ? 3 : 1;
  const enc = new TextEncoder();
  // TIFF ASCII tags must be 7-bit: transliterate common symbols, replace the rest.
  const ascii = (t: string) =>
    t
      .replace(/²/g, '^2')
      .replace(/³/g, '^3')
      .replace(/[µμ]/g, 'u')
      .replace(/×/g, 'x')
      .replace(/[—–−]/g, '-')
      .replace(/·/g, '.')
      .replace(/°/g, ' deg')
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/γ/g, 'gamma')
      .replace(/[^\x20-\x7e\n\r\t]/g, '?');
  const desc = img.description ? enc.encode(ascii(img.description) + '\0') : null;
  const soft = enc.encode(ascii(img.software ?? 'Aperto') + '\0');
  type Entry = { tag: number; type: number; count: number; value: number[] | Uint8Array };
  // Types: 2 ASCII, 3 SHORT, 4 LONG, 5 RATIONAL, 7 UNDEFINED.
  const res = Math.round((img.pixelsPerCm ?? 1) * 1000);
  const entries: Entry[] = [
    { tag: 256, type: 4, count: 1, value: [width] },
    { tag: 257, type: 4, count: 1, value: [height] },
    { tag: 258, type: 3, count: channels, value: Array(channels).fill(bits) },
    { tag: 259, type: 3, count: 1, value: [1] },
    { tag: 262, type: 3, count: 1, value: [channels === 1 ? 1 : 2] },
    ...(desc ? [{ tag: 270, type: 2, count: desc.length, value: desc }] : []),
    { tag: 273, type: 4, count: 1, value: [0] }, // patched below
    ...(img.orientation ? [{ tag: 274, type: 3, count: 1, value: [img.orientation] }] : []),
    { tag: 277, type: 3, count: 1, value: [channels] },
    { tag: 278, type: 4, count: 1, value: [height] },
    { tag: 279, type: 4, count: 1, value: [data.byteLength] },
    { tag: 282, type: 5, count: 1, value: [res, 1000] },
    { tag: 283, type: 5, count: 1, value: [res, 1000] },
    { tag: 284, type: 3, count: 1, value: [1] },
    { tag: 296, type: 3, count: 1, value: [img.pixelsPerCm ? 3 : 1] },
    { tag: 305, type: 2, count: soft.length, value: soft },
    { tag: 339, type: 3, count: channels, value: Array(channels).fill(sampleFormat) },
    ...(img.icc ? [{ tag: 34675, type: 7, count: img.icc.length, value: img.icc }] : []),
  ];
  const typeSize: Record<number, number> = { 2: 1, 3: 2, 4: 4, 5: 8, 7: 1 };
  const ifdOffset = 8;
  const ifdSize = 2 + entries.length * 12 + 4;
  let extra = ifdOffset + ifdSize;
  const extraOffsets = new Map<Entry, number>();
  for (const e of entries) {
    const sz = typeSize[e.type] * e.count;
    if (sz > 4) {
      extraOffsets.set(e, extra);
      extra += sz + (sz & 1);
    }
  }
  const dataOffset = extra + ((4 - (extra % 4)) % 4);
  entries.find((e) => e.tag === 273)!.value = [dataOffset];
  const out = new Uint8Array(dataOffset + data.byteLength);
  const dv = new DataView(out.buffer);
  out[0] = out[1] = 0x49;
  dv.setUint16(2, 42, true);
  dv.setUint32(4, ifdOffset, true);
  dv.setUint16(ifdOffset, entries.length, true);
  const writeVals = (e: Entry, at: number) => {
    if (e.value instanceof Uint8Array) {
      out.set(e.value, at);
      return;
    }
    e.value.forEach((v, i) => {
      if (e.type === 3) dv.setUint16(at + i * 2, v, true);
      else dv.setUint32(at + i * 4, v, true);
    });
  };
  entries.forEach((e, i) => {
    const o = ifdOffset + 2 + i * 12;
    dv.setUint16(o, e.tag, true);
    dv.setUint16(o + 2, e.type, true);
    dv.setUint32(o + 4, e.count, true);
    const off = extraOffsets.get(e);
    if (off !== undefined) {
      dv.setUint32(o + 8, off, true);
      writeVals(e, off);
    } else writeVals(e, o + 8);
  });
  dv.setUint32(ifdOffset + 2 + entries.length * 12, 0, true);
  out.set(new Uint8Array(data.buffer, data.byteOffset, data.byteLength), dataOffset);
  return out;
}

/** Portable Float Map (little-endian, rows bottom-to-top as the format requires). */
export function encodePfm(width: number, height: number, channels: 1 | 3, data: Float32Array): Uint8Array {
  const head = new TextEncoder().encode(`${channels === 3 ? 'PF' : 'Pf'}\n${width} ${height}\n-1.0\n`);
  const out = new Uint8Array(head.length + data.length * 4);
  out.set(head);
  const dv = new DataView(out.buffer, head.length);
  const row = width * channels;
  for (let y = 0; y < height; y++) {
    const src = (height - 1 - y) * row;
    for (let i = 0; i < row; i++) dv.setFloat32((y * row + i) * 4, data[src + i], true);
  }
  return out;
}
